import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Idea, IdeaCreate, IdeaPromote, IdeaUpdate } from '@travel-planner/shared';
import { api } from '../api/client';

export const ideasKey = (tripId: string) => ['ideas', tripId] as const;

export function useIdeas(tripId: string | null) {
  return useQuery<Idea[]>({
    queryKey: tripId ? ideasKey(tripId) : ['ideas', 'none'],
    queryFn: () => api.get<Idea[]>(`/trips/${tripId}/ideas`),
    enabled: Boolean(tripId),
  });
}

export function useCreateIdea(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IdeaCreate) => api.post<Idea>(`/trips/${tripId}/ideas`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ideasKey(tripId) }),
  });
}

export function useUpdateIdea(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ideaId, patch }: { ideaId: string; patch: IdeaUpdate }) =>
      api.patch<Idea>(`/trips/${tripId}/ideas/${ideaId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ideasKey(tripId) }),
  });
}

export function useDeleteIdea(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ideaId: string) => api.del<void>(`/trips/${tripId}/ideas/${ideaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ideasKey(tripId) }),
  });
}

export function usePromoteIdea(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ideaId, input }: { ideaId: string; input: IdeaPromote }) =>
      api.post(`/trips/${tripId}/ideas/${ideaId}/promote`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ideasKey(tripId) });
      // The new list item lands in one country's board.
      qc.invalidateQueries({ queryKey: ['items', tripId] });
    },
  });
}
